import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, FormControl, Select, MenuItem, TextField, TablePagination, CircularProgress, Switch } from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { handleUnauthorized } from '../../TokenAuth/auth';
import { formatUTCDate } from '../../utils/dateUtils';
import { getIntParam, getStringParam, setIfTruthy } from '../../utils/urlState';

function ConsultationListTable() {
    const [searchParams, setSearchParams] = useSearchParams();
    const didInitUrlSyncRef = React.useRef(false);
    const [campaignStateData, setCampaignStateData] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [filter, setFilter] = useState(getStringParam(searchParams, "filter", "All"));
    const [isExporting, setIsExporting] = useState(false);
    const [loading, setLoading] = useState(false);
    const BASE_URL = process.env.REACT_APP_BASEURL;
    const getAuthToken = () => localStorage.getItem("astroVani-admin-token");
    const [paginationModel, setPaginationModel] = useState({
        pageSize: getIntParam(searchParams, "pageSize", 10),
        page: getIntParam(searchParams, "page", 0),
    });
    const [paginationDetail, setPaginationDetail] = useState({
        totalDocs: 0,
        totalPages: 0
    });

    const campaignList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: page,
                limit: limit,
                ...(filter !== "All" && { category: filter })
            });

            const response = await fetch(`${BASE_URL}/admin/rechargeCampaign?${queryParams}`, {
                headers: { 'Authorization': getAuthToken() },
            });

            if (!response.ok) throw new Error('Failed to fetch campaigns');
            if (response.status === 401) {
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const data = await response.json();
            setCampaignStateData(data?.data || []);
            setPaginationDetail({
                totalDocs: data?.paginationDetail?.totalDocs || 0,
                totalPages: data?.paginationDetail?.totalPages || 0
            });
        } catch (error) {
            toast.error(error.message || "Failed to fetch campaigns");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const page = paginationModel.page + 1;
        let limit = Number(paginationModel.pageSize);
        if (isNaN(limit)) {
            limit = paginationDetail?.totalDocs || 10000;
        }

        campaignList(page, limit);
    }, [paginationModel, filter, paginationDetail?.totalDocs]);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }

        const params = new URLSearchParams();
        params.set("page", String(paginationModel.page));
        params.set("pageSize", String(paginationModel.pageSize));
        setIfTruthy(params, "filter", filter);
        setSearchParams(params, { replace: true });
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        filter,
        setSearchParams,
    ]);

    const handleDelete = async () => {
        if (selectedCampaignId) {
            try {
                const response = await fetch(`${BASE_URL}/admin/rechargeCampaign/${selectedCampaignId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': getAuthToken() }
                });
                if (!response.ok) throw new Error("Failed to delete campaign");
                toast.success("Campaign deleted successfully!");
                campaignList(paginationModel.page + 1, paginationModel.pageSize);
            } catch (error) {
                toast.error(error.message || "Failed to delete!");
            }
        }
        setOpen(false);
    };

    // Pagination Handlers
    const handleChangePage = (event, newPage) =>
        setPaginationModel((prev) => ({ ...prev, page: newPage }));

    const handleChangeRowsPerPage = (event) => {
        const value = event.target.value;
        const newSize = parseInt(value, 10);
        setPaginationModel({ page: 0, pageSize: newSize });
    };


    const handleExport = async () => {
        setIsExporting(true);
        try {
            const token = getAuthToken();
            if (!token) {
                toast.error("Authorization token not found");
                setIsExporting(false);
                return;
            }

            const queryParams = new URLSearchParams({
                ...(filter !== "All" && { category: filter })
            });

            const response = await axios.get(`${BASE_URL}/admin/rechargeCampaign?${queryParams}`, {
                headers: { 'Authorization': token },
            });

            if (response.status === 401) {
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const allData = response.data?.data || [];

            if (!Array.isArray(allData) || allData.length === 0) {
                setIsExporting(false);
                return;
            }

            const exportData = allData.map((item, index) => ({
                "S.No": index + 1,
                Category: item.category || "",
                "Bonus Type": item.bonusType || "",
                "Bonus Value": item.bonusValue || 0,
                "Min Recharge Amount": item.minimumRechargeAmount || 0,
                "Start Date": item.startDate ? formatUTCDate(item.startDate, { format: 'date', separator: '/' }) : "",
                "End Date": item.endDate ? formatUTCDate(item.endDate, { format: 'date', separator: '/' }) : "",
                "Start Time": item.startTime || "",
                "End Time": item.endTime || "",
                Status: item.status || "",
                "Created Date": formatUTCDate(item?.createdAt, { format: 'date', separator: '/' }),
                "Created Time": formatUTCDate(item?.createdAt, { format: 'time', showSeconds: true }),
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Campaigns");
            XLSX.writeFile(workbook, `CampaignList_${new Date().toISOString().split('T')[0]}.xlsx`);
            toast.success("Export successful!");
        } catch (error) {
            console.error("Export error:", error);
            toast.error(error.response?.data?.message || `Export failed: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    };

    const rows = useMemo(() => {
        const pageSize = paginationModel.pageSize;
        return campaignStateData.map((item, index) => ({
            id: item._id || index,
            sono: index + 1 + paginationModel.page * pageSize,
            _id: item._id,
            category: item?.category || "N/A",
            bonusType: item?.bonusType || "N/A",
            bonusValue: item?.bonusValue || 0,
            minimumRechargeAmount: item?.minimumRechargeAmount || 0,
            startDate: item?.startDate ? new Date(item.startDate).toLocaleDateString() : "N/A",
            endDate: item?.endDate ? new Date(item.endDate).toLocaleDateString() : "N/A",
            startTime: item?.startTime || "N/A",
            endTime: item?.endTime || "N/A",
            status: item?.status || "inactive",
            createdAt: item?.createdAt ? new Date(item?.createdAt) : null,
            updatedAt: item?.updatedAt ? new Date(item?.updatedAt) : null,
        }));
    }, [campaignStateData, paginationModel]);

    const handleStatusChange = async (row) => {
        try {
            const newStatus = row.status === 'active' ? 'inactive' : 'active';
            const response = await fetch(`${BASE_URL}/admin/rechargeCampaign/status/${row._id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': getAuthToken(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) throw new Error('Failed to update status');
            if (response.status === 401) {
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }
            toast.success("Status updated successfully!");
            campaignList(paginationModel.page + 1, paginationModel.pageSize);
        } catch (error) {
            toast.error(error.message || "Failed to update status!");
        }
    }

    const columns = [
        { field: 'sono', headerName: 'S.No', width: 80, headerAlign: 'center', align: 'center' },
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 140,
        //     sortable: false,
        //     headerAlign: 'center',
        //     align: 'center',
        //     renderCell: ({ row }) => (
        //         <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
        //             <IconButton component={Link} to={`/campaign-edit/${row._id}`} title="Edit">
        //                 <i className="icofont-edit" style={{ fontSize: '18px', color: '#1976d2' }}></i>
        //             </IconButton>
        //             <IconButton onClick={() => { setSelectedCampaignId(row._id); setOpen(true); }} title="Delete">
        //                 <i className="icofont-ui-delete" style={{ fontSize: '18px', color: '#d32f2f' }}></i>
        //             </IconButton>
        //         </Box>
        //     ),
        // },
        { field: 'category', headerName: 'Category', width: 180 },
        { field: 'bonusType', headerName: 'Bonus Type', width: 130 },
        { field: 'bonusValue', headerName: 'Bonus Value', width: 120 },
        { field: 'minimumRechargeAmount', headerName: 'Min Recharge', width: 140 },
        { field: 'startDate', headerName: 'Start Date', width: 130 },
        { field: 'endDate', headerName: 'End Date', width: 130 },
        { field: 'startTime', headerName: 'Start Time', width: 120 },
        { field: 'endTime', headerName: 'End Time', width: 120 },
        {
            field: 'status', headerName: 'Status', width: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Switch
                        checked={row.status === 'active'}
                        color="primary"
                        size="small"
                        onChange={() => handleStatusChange(row)}
                        inputProps={{ 'aria-label': 'controlled' }}
                    />
                </Box>
            ),
        },
        { field: 'createdAt', headerName: 'Created At', width: 180, type: 'dateTime' },
    ];

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <PageHeader1 pagetitle="Campaign List" righttitle="Create New" link="/campaign-add" routebutton={true} />

                {/* Search and Export Toolbar */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <FormControl sx={{ minWidth: 220 }} size="small">
                            <Select
                                value={filter}
                                onChange={(e) => { setFilter(e.target.value); setPaginationModel(prev => ({ ...prev, page: 0 })); }}
                            >
                                <MenuItem value="All">All Categories</MenuItem>
                                <MenuItem value="First Recharge">First Recharge</MenuItem>
                                <MenuItem value="Second Recharge">Second Recharge</MenuItem>
                                <MenuItem value="Third Recharge">Third Recharge</MenuItem>
                                <MenuItem value="Fourth Recharge">Fourth Recharge</MenuItem>
                                <MenuItem value="Fifth Recharge">Fifth Recharge & onwards</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleExport}
                            disabled={isExporting}
                            className="btn btn-primary"
                            sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                height: 40,
                                px: 2,

                            }}
                        >
                            {isExporting ? (
                                <>
                                    <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                                    Exporting...
                                </>
                            ) : (
                                "Export to Excel"
                            )}
                        </Button>
                    </Box>
                </Box>

                {/* Table */}
                <div className="card" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
                    <div className="card-body" style={{ padding: 0 }}>
                        {/* Custom Pagination on Top */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                backgroundColor: "#f8f9fa",
                                borderBottom: "2px solid #e9ecef",
                            }}
                        >
                            <TablePagination
                                component="div"
                                count={paginationDetail?.totalDocs || 0}
                                page={paginationModel.page}
                                onPageChange={handleChangePage}
                                rowsPerPage={paginationModel.pageSize}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                            />
                        </Box>

                        <Box sx={{
                            width: '100%',
                            '& .MuiDataGrid-root': {
                                border: 'none',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f8f9fa',
                                borderBottom: '2px solid #e9ecef',
                                fontWeight: 600,
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#f8f9fa',
                            },
                        }}>
                            <DataGrid
                                autoHeight
                                columns={columns}
                                rows={rows}
                                loading={loading}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[10, 25, 50]}
                                paginationMode="server"
                                disableRowSelectionOnClick
                                getRowId={(row) => row.id}
                                hideFooterPagination
                                hideFooter
                                sx={{
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </div>
                </div>
            </div>

            {/* Delete Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this campaign?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ConsultationListTable;
